import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { LogOut, User, Shield, Settings, X, LayoutDashboard, Info, GraduationCap, Wrench, Link2, ClipboardList, LineChart, ShoppingBag, Hourglass } from "lucide-react";

const LOGO_URL = "/logo-gv.png";

const baseNavItems = [
  { path: "/dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { path: "/chi-siamo", label: "Chi siamo", Icon: Info },
  { path: "/servizi", label: "Percorso e Servizi", Icon: GraduationCap },
  { path: "/strumenti", label: "Strumenti", Icon: Wrench },
  { path: "/shop", label: "Shop", Icon: ShoppingBag, shopGate: true },
  { path: "/link-utili", label: "Link Utili", Icon: Link2 },
];

export default function Header({ isMenuOpen, setIsMenuOpen }) {
  const { logout, isAdmin, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [shopEnabled, setShopEnabled] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/settings/shop_enabled`)
      .then((res) => setShopEnabled(res.data.value === "true"))
      .catch(() => setShopEnabled(false));
  }, [user, isMenuOpen]);

  const handleNavClick = (item) => (e) => {
    if (item.shopGate && !isAdmin && !shopEnabled) {
      e.preventDefault();
      setIsMenuOpen(false);
      setShowComingSoon(true);
      return;
    }
    setIsMenuOpen(false);
  };

  const navItems = isAdmin
    ? [...baseNavItems, { path: "/admin/form-studenti", label: "Form Studenti", Icon: ClipboardList }, { path: "/admin/analisi-studenti", label: "Analisi Studenti", Icon: LineChart }, { path: "/admin/shop", label: "Gestione Shop", Icon: ShoppingBag }, { path: "/impostazioni", label: "Impostazioni", Icon: Settings }]
    : baseNavItems;

  const handleLogout = () => {
    logout();
    navigate("/login");
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Header */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 glass-card"
        style={{ 
          borderRadius: 0,
          borderTop: "none",
          borderLeft: "none",
          borderRight: "none",
          borderBottom: "1px solid var(--mm-border)"
        }}
        data-testid="header"
      >
        <div className="container flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3" data-testid="header-logo-link">
            <img 
              src={LOGO_URL} 
              alt="GoldVision Logo" 
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              data-testid="header-logo"
            />
            <span 
              className="text-lg sm:text-xl font-semibold hidden sm:block gold-text"
              style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.08em" }}
            >
              GOLDVISION
            </span>
          </Link>

          {/* User Badge + Hamburger */}
          <div className="flex items-center gap-4">
            {/* Role Badge */}
            <div 
              className="hidden md:flex items-center gap-2 px-3 py-1.5"
              style={{ 
                backgroundColor: isAdmin ? "rgba(47, 216, 168, 0.15)" : "rgba(255, 255, 255, 0.1)",
                border: `1px solid ${isAdmin ? "var(--mm-gold)" : "var(--mm-border)"}`
              }}
              data-testid="user-role-badge"
            >
              {isAdmin ? (
                <Shield size={14} style={{ color: "var(--mm-gold)" }} />
              ) : (
                <User size={14} style={{ color: "var(--mm-text-secondary)" }} />
              )}
              <span 
                className="text-xs font-semibold uppercase"
                style={{ 
                  color: isAdmin ? "var(--mm-gold)" : "var(--mm-text-secondary)",
                  fontFamily: "'Space Grotesk', sans-serif",
                  letterSpacing: "0.12em"
                }}
              >
                {isAdmin ? "Admin" : "Membro"}
              </span>
            </div>

            {/* Hamburger Menu */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`hamburger ${isMenuOpen ? "open" : ""}`}
              aria-label="Menu"
              data-testid="hamburger-menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </header>

      {/* Backdrop */}
      <div 
        className={`mobile-menu-overlay ${isMenuOpen ? "open" : ""}`}
        onClick={() => setIsMenuOpen(false)}
        data-testid="mobile-menu-overlay"
      />

      {/* Half-screen Drawer */}
      <aside className={`menu-drawer ${isMenuOpen ? "open" : ""}`} data-testid="menu-drawer">
        <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom: "1px solid var(--mm-border)" }}>
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="GoldVision" className="w-9 h-9 object-contain" />
            <div>
              <p className="text-sm font-semibold gold-text" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.1em" }}>GOLDVISION</p>
              <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>{isAdmin ? "Amministratore" : "Membro"}</p>
            </div>
          </div>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="p-2 transition-colors hover:bg-white/5"
            style={{ border: "1px solid var(--mm-border)" }}
            aria-label="Chiudi menu"
            data-testid="close-menu-btn"
          >
            <X size={18} style={{ color: "var(--mm-text-primary)" }} />
          </button>
        </div>

        <nav className="py-6">
          {navItems.map((item, idx) => {
            const active = location.pathname === item.path;
            const Icon = item.Icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick(item)}
                className="menu-item-giant"
                style={{ 
                  color: active ? "var(--mm-gold)" : "var(--mm-text-primary)",
                  borderLeftColor: active ? "var(--mm-gold)" : "transparent"
                }}
                data-testid={`nav-link-${item.path.replace(/\//g, "")}`}
              >
                {item.label}
              <Icon size={20} style={{ color: active ? "var(--mm-gold)" : "var(--mm-text-muted)", marginLeft: "auto" }} />
              </Link>
            );
          })}
        </nav>

        <div className="px-6 py-5 mt-2" style={{ borderTop: "1px solid var(--mm-border)" }}>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center gap-3 w-full py-3 transition-colors"
            style={{ 
              backgroundColor: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.5)",
              color: "#EF4444",
              fontFamily: "'Space Grotesk', sans-serif",
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              fontSize: "13px",
              fontWeight: 700
            }}
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            Esci
          </button>
        </div>
      </aside>

      {showComingSoon && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} onClick={() => setShowComingSoon(false)} data-testid="coming-soon-overlay">
          <div className="w-full max-w-sm p-8 text-center" style={{ backgroundColor: "#0b1512", border: "1px solid var(--mm-gold)" }} onClick={(e) => e.stopPropagation()} data-testid="coming-soon-popup">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "rgba(47,216,168,0.12)" }}>
              <Hourglass size={28} style={{ color: "var(--mm-gold)" }} />
            </div>
            <h2 className="text-2xl font-bold mb-2 gold-text" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.05em" }}>COMING SOON</h2>
            <p className="text-sm mb-6" style={{ color: "var(--mm-text-muted)" }}>Lo shop GoldVision sta arrivando. Torna a trovarci presto!</p>
            <button onClick={() => setShowComingSoon(false)} className="btn-primary text-sm w-full" data-testid="coming-soon-close">Ho capito</button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
